import {
  AcademicYearDto,
  CommentDto,
  IRegistrationClient,
  IRegistrationEditClient,
  IRegistrationExportClient,
  ModuleCategoryDto,
  ModuleDto,
  ModuleExecutionDto,
  ModuleRegistrationsDto,
  PhaseCapacityDto,
  RegistrationState,
  SemesterInfoDto,
} from '@mse-reg/clients';
import { saveAs } from 'file-saver';

import { createSlice, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';

import {
  addCommentToRegistrationInfo,
  createAppThunk,
  removeCommentFromRegistrationInfo,
  RootState,
} from '@mse-reg/app/store';
import { addExceptionNotification, addSuccess } from '../notification.slice';
import { moduleSelectionThunks } from '../selections/module-selection.slice';

const initialState = {
  loading: false,
  sidebarLoading: false,
  errorMessage: null,
  moduleRegistrationInfo: null as ModuleRegistrationsDto,
  selectedRequested: [] as number[],
  selectedConfirmed: [] as number[],
  selectedRejected: [] as number[],
  selectedWithdrawn: [] as number[],
  selectedAbsentex: [] as number[],
  selectedOthers: [] as number[],
  capacities: [] as PhaseCapacityDto[],
  priorityRequestId: null as string | null,
};

const fetchPrioritiesAsync = createAppThunk(
  'moduleRegistrations/FETCH_MODULE_REGISTRATIONS',
  async (moduleExecutionId: number, deps: { registrationAdminClient: IRegistrationClient }) => {
    if (moduleExecutionId) {
      return deps.registrationAdminClient.getPriorities(moduleExecutionId);
    }
    return null;
  },
  (r) => ({ registrationAdminClient: r.resolve('IRegistrationClient') }),
);

const fetchCapacitiesAsync = createAppThunk(
  'moduleRegistrations/FETCH_CAPACITIES',
  async (semesterId: number | undefined, deps: { registrationAdminClient: IRegistrationClient }) => {
    if (semesterId) {
      return deps.registrationAdminClient.getCapacities(semesterId);
    }
    return [];
  },
  (r) => ({ registrationAdminClient: r.resolve('IRegistrationClient') }),
);

const changeRegistrationStatesInPrioritizationAsync = createAppThunk(
  'moduleRegistrations/CHANGE_STATES',
  async (
    args: { registrationState: RegistrationState; comment: string; ids: number[] },
    deps: { registrationAdminClient: IRegistrationClient },
    dispatch,
    getState,
  ) => {
    const result = await deps.registrationAdminClient.changeRegistrationStatesInPrioritization(
      args.ids,
      args.registrationState,
      args.comment,
    );
    const selectedExecution = getState().moduleSelection.selectedExecution;
    await Promise.all([
      selectedExecution
        ? dispatch(fetchPrioritiesAsync(selectedExecution.id)).unwrap()
        : Promise.resolve(),
      dispatch(fetchCapacitiesAsync(getState().semesterSelection.selectedSemester?.id)).unwrap(),
    ]);
    return result;
  },
  (r) => ({ registrationAdminClient: r.resolve('IRegistrationClient') }),
);

const getExecutionsAsync = createAppThunk(
  'prioritization/GET_EXECUTIONS',
  async (
    args: {
      category?: ModuleCategoryDto;
      semester?: SemesterInfoDto;
    },
    deps,
    dispatch,
  ) => {
    try {
      if (!args.semester) {
        return [];
      }
      return await dispatch(
        moduleSelectionThunks.getExecutionsAsync({
          academicYearId: args.semester.academicYearId,
          semesterId: args.semester.id,
        }),
      ).unwrap();
    } catch (err) {
      dispatch(addExceptionNotification(err));
      return [];
    }
  },
);

const exportAsync = createAppThunk(
  'prioritization/EXPORT',
  async (moduleExecutionId: number, deps: { registrationExportClient: IRegistrationExportClient }) => {
    const fileResponse = await deps.registrationExportClient.exportRegistrationsForModuleExecution(moduleExecutionId);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ registrationExportClient: r.resolve('IRegistrationExportClient') }),
);

const autoConfirmAsync = createAppThunk(
  'prioritization/AUTO_CONFIRM',
  async (
    args: { semesterId: number; categoryId: number },
    deps: { registrationClient: IRegistrationClient },
    dispatch,
    getState,
  ) => {
    try {
      await deps.registrationClient.autoConfirm(args.semesterId, args.categoryId);
      dispatch(addSuccess('Successfully confirmed registrations.'));
      const semesterSelection = getState().semesterSelection;
      await dispatch(fetchCapacitiesAsync(semesterSelection.selectedSemester?.id));
      const moduleSelection = getState().moduleSelection;
      if (moduleSelection.selectedExecution) {
        await dispatch(fetchPrioritiesAsync(moduleSelection.selectedExecution.id));
      }
    } catch (err) {
      dispatch(addExceptionNotification(err));
    }
  },
  (r) => ({ registrationClient: r.resolve('IRegistrationClient') }),
);

const addCommentAsync = createAppThunk(
  'moduleRegistrations/ADD_REGISTRATION_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      const id = await deps.registrationEditClient.addComment(comment);
      dispatch(addSuccess(`Successfully added comment.`));
      return { ...comment, id } as CommentDto;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
    }
    return undefined;
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') }),
);

const removeCommentAsync = createAppThunk(
  'moduleRegistrations/REMOVE_REGISTRATION_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      await deps.registrationEditClient.deleteComment(comment.id);
      dispatch(addSuccess(`Successfully deleted comment.`));
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
    }
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') }),
);

const getHistoryAsync = createAppThunk(
  'prioritization/GET_HISTORY',
  async (moduleRegistrationId: number, deps: { registrationEditClient: IRegistrationEditClient }) => {
    return await deps.registrationEditClient.getHistory(moduleRegistrationId);
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') }),
);

const slice = createSlice({
  name: 'prioritization',
  initialState,
  reducers: {
    setRequestedSelected(state, action: PayloadAction<number[]>) {
      state.selectedRequested = action.payload;
    },
    setConfirmedSelected(state, action: PayloadAction<number[]>) {
      state.selectedConfirmed = action.payload;
    },
    setAbsentexSelected(state, action: PayloadAction<number[]>) {
      state.selectedAbsentex = action.payload;
    },
    setRejectedSelected(state, action: PayloadAction<number[]>) {
      state.selectedRejected = action.payload;
    },
    setWithdrawnSelected(state, action: PayloadAction<number[]>) {
      state.selectedWithdrawn = action.payload;
    },
    setOthersSelected(state, action: PayloadAction<number[]>) {
      state.selectedOthers = action.payload;
    },
    upsertCapacity(
      state,
      action: PayloadAction<
        Pick<PhaseCapacityDto, 'moduleExecutionId' | 'confirmedCount' | 'requestedCount' | 'waitingCount'>
      >,
    ) {
      const capacity = state.capacities.find((c) => c.moduleExecutionId === action.payload.moduleExecutionId);
      if (capacity) {
        capacity.confirmedCount = action.payload.confirmedCount;
        capacity.requestedCount = action.payload.requestedCount;
        capacity.waitingCount = action.payload.waitingCount;
        capacity.free = Math.max(0, capacity.currentCapacity - capacity.confirmedCount);
        return;
      }

      state.capacities.push({
        moduleExecutionId: action.payload.moduleExecutionId,
        confirmedCount: action.payload.confirmedCount,
        requestedCount: action.payload.requestedCount,
        waitingCount: action.payload.waitingCount,
        free: 0,
        currentCapacity: 0,
        fullCapacity: 0,
        reservePercentage: 0,
        phaseId: null,
      });
    },
    addRegistrationComment(state, action: PayloadAction<CommentDto>) {
      if (!action.payload.registrationId) {
        return;
      }

      state.moduleRegistrationInfo.confirmed = addCommentToRegistrationInfo(
        state.moduleRegistrationInfo.confirmed,
        action.payload,
      );
      state.moduleRegistrationInfo.requested = addCommentToRegistrationInfo(
        state.moduleRegistrationInfo.requested,
        action.payload,
      );
      state.moduleRegistrationInfo.others = addCommentToRegistrationInfo(
        state.moduleRegistrationInfo.others,
        action.payload,
      );
    },
    removeRegistrationComment(state, action: PayloadAction<CommentDto>) {
      if (!action.payload.registrationId) {
        return;
      }

      state.moduleRegistrationInfo.confirmed = removeCommentFromRegistrationInfo(
        state.moduleRegistrationInfo.confirmed,
        action.payload,
      );
      state.moduleRegistrationInfo.requested = removeCommentFromRegistrationInfo(
        state.moduleRegistrationInfo.requested,
        action.payload,
      );
      state.moduleRegistrationInfo.others = removeCommentFromRegistrationInfo(
        state.moduleRegistrationInfo.others,
        action.payload,
      );
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCapacitiesAsync.pending, (state) => {
        state.sidebarLoading = true;
        state.capacities = [];
      })
      .addCase(fetchCapacitiesAsync.rejected, (state, action) => {
        state.sidebarLoading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(fetchCapacitiesAsync.fulfilled, (state, action) => {
        state.sidebarLoading = false;
        state.capacities = action.payload;
      })
      .addCase(fetchPrioritiesAsync.pending, (state, action) => {
        state.loading = true;
        state.priorityRequestId = action.meta.requestId;
      })
      .addCase(fetchPrioritiesAsync.rejected, (state, action) => {
        if (state.priorityRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.errorMessage = action.error.message;
        state.priorityRequestId = null;
      })
      .addCase(fetchPrioritiesAsync.fulfilled, (state, action) => {
        if (state.priorityRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.priorityRequestId = null;
        state.selectedRequested = action.payload.requested?.filter((r) => r.isSelected).map((r) => r.id) ?? [];
        state.selectedConfirmed = action.payload.confirmed?.filter((r) => r.isSelected).map((r) => r.id) ?? [];
        state.selectedRejected =
          action.payload.others?.filter((r) => r.isSelected && r.state === 'REJECTED').map((r) => r.id) ?? [];
        state.selectedWithdrawn =
          action.payload.others?.filter((r) => r.isSelected && r.state === 'WITHDRAWN').map((r) => r.id) ?? [];
        state.selectedOthers =
          action.payload.others
            ?.filter((r) => r.isSelected && r.state !== 'WITHDRAWN' && r.state !== 'REJECTED' && r.state !== 'ABSENTEX')
            .map((r) => r.id) ?? [];
        state.selectedAbsentex =
          action.payload.others?.filter((r) => r.state === 'ABSENTEX').map((r) => r.id) ?? [];
        state.moduleRegistrationInfo = action.payload;
      })
      .addCase(getExecutionsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getExecutionsAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getExecutionsAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeRegistrationStatesInPrioritizationAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeRegistrationStatesInPrioritizationAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(changeRegistrationStatesInPrioritizationAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(exportAsync.fulfilled, (state) => {
        state.loading = false;
      });
  },
});

export const prioritizationReducer = slice.reducer;
export const prioritizationActions = slice.actions;
export const prioritizationThunks = {
  getExecutionsAsync,
  changeRegistrationStatesInPrioritizationAsync,
  fetchCapacitiesAsync,
  exportAsync,
  addCommentAsync,
  removeCommentAsync,
  getHistoryAsync,
};

export const mapDispatchToPropsPrioritization = (dispatch: ThunkDispatch<RootState, any, any>) => ({
  setFiltersAsync: async (args: {
    category?: ModuleCategoryDto;
    module?: ModuleDto;
    moduleExecutionId?: number;
    semester?: SemesterInfoDto;
    academicYear?: AcademicYearDto;
  }) => await dispatch(moduleSelectionThunks.setFilterAsync(args)).unwrap(),
  getExecutionsAsync: async (args: { category?: ModuleCategoryDto; semester?: SemesterInfoDto }) =>
    await dispatch(getExecutionsAsync(args)).unwrap(),
  fetchCapacitiesForSemesterAsync: async (semesterId: number | undefined) =>
    await dispatch(fetchCapacitiesAsync(semesterId)).unwrap(),
  reloadExecutionsAsync: () =>
    dispatch((d: ThunkDispatch<RootState, any, any>, getState: () => RootState) => {
      const semester = getState().semesterSelection.selectedSemester;
      if (!semester) return Promise.resolve([]);
      return d(getExecutionsAsync({ semester })).unwrap();
    }),
  changeRegistrationStatesAsync: async (registrationState: RegistrationState, comment: string, selected: number[]) =>
    await dispatch(
      changeRegistrationStatesInPrioritizationAsync({ registrationState, comment, ids: selected }),
    ).unwrap(),
  async loadDataAsync(moduleExecution?: ModuleExecutionDto) {
    await dispatch(fetchPrioritiesAsync(moduleExecution?.id)).unwrap();
  },
  setRequestedSelected: (selected: number[]) => dispatch(slice.actions.setRequestedSelected(selected)),
  setConfirmedSelected: (selected: number[]) => dispatch(slice.actions.setConfirmedSelected(selected)),
  setRejectedSelected: (selected: number[]) => dispatch(slice.actions.setRejectedSelected(selected)),
  setAbsentexSelected: (selected: number[]) => dispatch(slice.actions.setAbsentexSelected(selected)),
  setWithdrawnSelected: (selected: number[]) => dispatch(slice.actions.setWithdrawnSelected(selected)),
  setOthersSelected: (selected: number[]) => dispatch(slice.actions.setOthersSelected(selected)),
  upsertCapacity: (capacity: Pick<PhaseCapacityDto, 'moduleExecutionId' | 'confirmedCount' | 'requestedCount' | 'waitingCount'>) =>
    dispatch(slice.actions.upsertCapacity(capacity)),
  exportAsync: async (executionId: number) => await dispatch(exportAsync(executionId)).unwrap(),
  autoConfirmAsync: async (semesterId: number, categoryId: number) =>
    await dispatch(autoConfirmAsync({ semesterId, categoryId })).unwrap(),
  addCommentAsync: async (comment: CommentDto) => await dispatch(addCommentAsync(comment)).unwrap(),
  removeCommentAsync: async (comment: CommentDto) => await dispatch(removeCommentAsync(comment)).unwrap(),
  getHistoryAsync: async (moduleRegistrationId: number) =>
    await dispatch(getHistoryAsync(moduleRegistrationId)).unwrap(),
});
